<template>
  <iColumn>
    <iSubHeading>Apply to collaborate</iSubHeading>
    <iRow gap="small" align="left" vertical-align="middle">
      <iColumn>
        <iText v-if="subtleText" variant="subtle">
          {{ subtleText }}
        </iText>
        <iButton
          v-else-if="publication.enable_applications === 'enabled'"
          size="small"
          variant="secondary"
          @click="onApplyClick"
        >
          Apply Now
        </iButton>
        <iText v-else>
          This publication is not accepting applications for new collaborations at this time
        </iText>
      </iColumn>
    </iRow>
  </iColumn>

  <iModal
    v-if="applyToCollaborateModalOpen"
    title="Apply to Collaborate"
    :max-width="900"
    @close="closeModal"
  >
    <template #body>
      <iRow v-if="dummyCollaborationSow">
        <iSubHeading>Collaboration</iSubHeading>
      </iRow>
      <iColumn gap="large">
        <iColumn v-if="!showRoleText" gap="none">
          <iSelect
            v-model="selectedRole"
            :items="ROLE_OPTIONS"
            label="Role"
            width="fill"
            value-field="value"
          />
          <iText v-if="validationErrors.role" variant="error">
            {{ validationErrors.role }}
          </iText>
        </iColumn>
        <iSubHeading v-else width="fill">
          You are applying to be a {{ preSelectedRole }}
        </iSubHeading>

        <iColumn gap="none">
          <iTextArea
            v-model="applicationMessage"
            label="Application Message"
            placeholder="Enter your message here..."
            :rows="5"
            width="fill"
          />
          <iText v-if="validationErrors.message" variant="error">
            {{ validationErrors.message }}
          </iText>
        </iColumn>

        <iText v-if="error" variant="error">
          {{ error }}
        </iText>
        <CollaborationContractProposal v-if="dummyCollaborationSow" />
      </iColumn>
    </template>
    <template #footer>
      <iRow align="between" gap="standard" width="fill">
        <iColumn width="hug">
          <iButton
            variant="secondary"
            @click="closeModal"
          >
            Cancel
          </iButton>
        </iColumn>
        <iColumn width="hug">
          <iButton
            variant="primary"
            loading-spinner-position="inside"
            :is-loading="isCreatingApplication"
            :disabled="isDisabled"
            @click="submitApplication"
          >
            Submit Application
          </iButton>
        </iColumn>
      </iRow>
    </template>
  </iModal>
</template>
<script>
import { mapState, mapActions, mapWritableState } from "pinia";
import { useCollaboratorStore } from "@/stores/collaborator-store";
import { useOrganizationStore } from "@/stores/organization-store";
import { useUserStore } from "@/stores/user-store";
import { ROLE_OPTIONS } from "@/constants/collaborator-constants";
import CollaborationContractProposal from "@/components/CollaborationContractProposal.vue";
import { useFeatureStore } from "%/stores/feature-store";

export default {
  name: "ApplyToCollaborateProfile",
  components: { CollaborationContractProposal },
  props: {
    publication: {
      type: Object,
      required: true,
    },
    collaboratorRelationship: {
      type: String,
      default: "none",
    },
  },
  data() {
    return {
      ROLE_OPTIONS,
      preSelectedRole: this.defaultRole,
      applyToCollaborateModalOpen: false,
      selectedRole: {},
      applicationMessage: "",
      validationErrors: {
        role: "",
        message: "",
      },
    };
  },
  computed: {
    dummyCollaborationSow() {
      return useFeatureStore().isFeatureEnabled("dummyCollaborationSow");
    },
    ...mapState(useUserStore, ["userData", "emailData"]),
    ...mapState(useOrganizationStore, ["getOrgData"]),
    ...mapState(useCollaboratorStore, [
      "isCreatingApplication",
    ]),
    ...mapWritableState(useCollaboratorStore, ["error"]),
    isFormValid() {
      return (
        (this.selectedRole.value || this.preSelectedRole)
          && this.applicationMessage
      );
    },
    isDisabled() {
      return !this.isFormValid || this.isCreatingApplication;
    },
    shouldDisableButton() {
      return this.collaboratorRelationship === "owner" ||
          this.collaboratorRelationship === "collaborator" ||
          this.collaboratorRelationship === "applicant" ||
          this.collaboratorRelationship === "applicant_rejected";
    },
    subtleText() {
      switch (this.collaboratorRelationship) {
        case "owner":
          return "You are the owner of this publication";
        case "collaborator":
          return "You are already a collaborator";
        case "applicant":
          return "Your application is pending review";
        case "invited":
          return "You have been invited to collaborate on this publication";
        case "applicant_rejected":
          return "Unfortunately your application was not accepted";
        default:
          return "";
      }
    },
  },
  methods: {
    ...mapActions(useCollaboratorStore, [
      "createApplication",
    ]),
    onApplyClick() {
      if (!this.shouldDisableButton) {
        this.openModal();
      }
    },
    openModal() {
      this.applyToCollaborateModalOpen = true;
      this.error = null;
    },
    closeModal() {
      this.applyToCollaborateModalOpen = false;
      this.selectedRole = {};
      this.applicationMessage = "";
      this.error = null;
      this.validationErrors.role = "";
      this.validationErrors.message = "";
    },
    async submitApplication() {
      this.validationErrors.role = "";
      this.validationErrors.message = "";

      if (!this.showRoleText && !this.selectedRole.value) {
        this.validationErrors.role = "Please select a role";
      }

      if (!this.applicationMessage) {
        this.validationErrors.message = "Application message is required";
      }

      if (this.validationErrors.role || this.validationErrors.message) {
        return;
      }

      await this.createApplication(
        this.publication.publication_id,
        this.getOrgData.org_id,
        this.emailData.email_address,
        this.userData.first_name + " " + this.userData.last_name,
        this.selectedRole.value || this.preSelectedRole,
        this.applicationMessage,
        this.closeModal,
      );
    },
  },
};
</script>
